// Shared contract between client and API. Only wire-crossing types belong here.

// ---------------------------------------------------------------------------
// Auth / Identity
// ---------------------------------------------------------------------------

export interface Me {
  oid: string;
  email: string;
  displayName: string;
  roles: string[];
  /**
   * Present only when this session is impersonating a fixture user — the fields
   * above then describe the borrowed identity, and this is the human actually
   * signed in. Server-sourced on purpose: it confirms the override really took
   * effect, rather than the client assuming it did. See IMPERSONATION_PROFILES.
   */
  impersonatedBy?: { oid: string; displayName: string };
}

export const ROLE_ADMIN = 'admin';
export const ROLE_LOAN_ANALYST = 'loan_analyst';
export const ROLE_SPECIAL_ASSETS = 'special_assets';

// A user picked from a Graph group-member listing (for assignment dropdowns).
export interface GroupMember {
  id: string;          // Entra oid
  displayName: string;
  email: string | null;
}

// ---------------------------------------------------------------------------
// Impersonation — NON-PRODUCTION TESTING AID
// ---------------------------------------------------------------------------
//
// Lets a UAT tester act as one of the seeded fixture users so they can see the
// app as that role AND as that identity. Identity matters as much as role here:
// large parts of the app filter on the caller's oid rather than their roles
// (`requests.submittedByOid`, `assignedSaOfficerId`, the SA "My Assignments"
// queue). A role-only override would render an SA view with an empty work
// queue, which is exactly the confusing half-answer we're trying to avoid.
//
// THIS IS A REAL PRIVILEGE GRANT, not a UI toggle: impersonating grants roles
// the signed-in user does not have. It is therefore gated server-side by the
// IMPERSONATION_ENABLED app setting and FAILS CLOSED — the header is ignored
// unless that setting is explicitly "true". It is never set in the production
// pipeline, and `shared/src/index.test.ts` asserts the disabled default.

export const IMPERSONATION_HEADER = 'x-impersonate-user';

export interface ImpersonationProfile {
  key: string;         // stable id sent in the header
  oid: string;         // matches the fixture oids in database/seed-testdata.mjs
  displayName: string;
  email: string;
  roles: string[];
}

/**
 * The fixture roster. `oid` values intentionally match the ANALYSTS and
 * SA_OFFICERS arrays in `database/seed-testdata.mjs` so that impersonating a
 * profile lands on a user who actually owns seeded loans, requests, and
 * assignments. If you change an oid here, change it there too.
 */
export const IMPERSONATION_PROFILES: ImpersonationProfile[] = [
  {
    key: 'admin',
    oid: '99999999-9999-9999-9999-999999999999',
    displayName: 'Ada Admin (test)',
    email: 'ada.admin@example.test',
    roles: [ROLE_ADMIN],
  },
  {
    key: 'analyst-mark',
    oid: '11111111-1111-1111-1111-111111111111',
    displayName: "Mark L'Ecuyer (test)",
    email: 'mark.lecuyer@example.test',
    roles: [ROLE_LOAN_ANALYST],
  },
  {
    key: 'analyst-sarah',
    oid: '22222222-2222-2222-2222-222222222222',
    displayName: 'Sarah James (test)',
    email: 'sarah.james@example.test',
    roles: [ROLE_LOAN_ANALYST],
  },
  {
    key: 'sa-rich',
    oid: '33333333-3333-3333-3333-333333333333',
    displayName: 'Rich Rulli (test)',
    email: 'rich.rulli@example.test',
    roles: [ROLE_SPECIAL_ASSETS],
  },
  {
    key: 'sa-diane',
    oid: '44444444-4444-4444-4444-444444444444',
    displayName: 'Diane Park (test)',
    email: 'diane.park@example.test',
    roles: [ROLE_SPECIAL_ASSETS],
  },
  {
    key: 'sa-lea',
    oid: '66666666-6666-6666-6666-666666666666',
    displayName: 'Lea Bennett (test)',
    email: 'lea.bennett@example.test',
    roles: [ROLE_SPECIAL_ASSETS],
  },
];

/**
 * Pure resolver for an impersonation request. Returns the profile to act as, or
 * null to act as the real signed-in user.
 *
 * Roles REPLACE rather than augment the caller's own: an admin impersonating a
 * Special Assets rep must lose admin, or they'd never see the restricted view
 * (see the admin pass-through in `client/src/auth/RoleGuard.tsx`).
 */
export interface ImpersonationConfig {
  enabled: boolean;
  profiles: ImpersonationProfile[];
}

export function resolveImpersonation(
  enabled: boolean,
  key: string | null | undefined,
): ImpersonationProfile | null {
  if (enabled !== true) return null;
  if (!key) return null;
  return IMPERSONATION_PROFILES.find(p => p.key === key) ?? null;
}

// ---------------------------------------------------------------------------
// Loans
// ---------------------------------------------------------------------------

// The loan's lifecycle *phase* — the single status users read at a glance.
// Detail about what is being worked (which package, how far along) lives on the
// package/request records, NOT here. See docs/2026-06-24 Requirements/status-model-redesign.md.
//
// Loans enter as 'Prepurchase' from the monthly extract. Phase advances when a
// phase-driving package is created or decisioned — see PACKAGE_META, which says
// per type which of the two applies. 'Closed' is terminal.
export type LoanPhase =
  | 'Prepurchase'   // delinquent; no purchase request submitted yet
  | 'Liquidation'   // purchase requested or completed; Special Assets works the recovery
  | 'Charge Off'    // charge-off package in progress / under SBA review
  | 'Closed';       // resolved — see closedReason

export const LOAN_PHASES: LoanPhase[] = ['Prepurchase', 'Liquidation', 'Charge Off', 'Closed'];

// Set only when phase === 'Closed'. Records how the loan resolved.
// How a loan ended. "Returned to Regular Servicing" used to be one of these and is not
// any more (#45) — a loan back on regular servicing is still ours to watch, so it stays in
// Liquidation with that as its Loan Status. Migration 028 moved the loans that had it.
export type ClosedReason =
  | 'SBA Charged Off'
  | 'SBA Uncollectible'
  | 'Paid in Full';

export const CLOSED_REASONS: ClosedReason[] = [
  'SBA Charged Off', 'SBA Uncollectible', 'Paid in Full',
];

export interface Loan {
  id: number;

  sbaLoanNumber: string;
  umwsbLoanNumber: string | null;
  borrowerName: string;

  // Record Type code from Loan Manager: '2' = Loan, '4' = Liquidation. Admin-visible.
  loanType: string | null;

  stateLocated: string | null;
  naicsCode: string | null;
  // Derived, never stored: resolved from naicsCode against dbo.naicsCodes on read
  // (migration 030). Read-only everywhere — editing the code changes it.
  naicsDescription: string | null;
  approvalGross: number | null;
  guarantyBalance: number | null;   // Part Bal (Bor) from the extract
  guarantyPct: number | null;
  dateFirstDisbursed: string | null;
  maturityDate: string | null;
  inBusiness: boolean | null;
  sbaLoanStatus: string | null;
  etranLoanStatus: string | null;
  legalBalance: number | null;
  nextDueDate: string | null;
  dateInLiquidation: string | null; // DATEINLIQUID from the extract
  dateOfDefault: string | null;
  interestRate: number | null;
  noteDate: string | null;

  // Lifecycle phase — the single status users read. See LoanPhase.
  phase: LoanPhase;
  closedReason: ClosedReason | null;            // set only when phase === 'Closed'
  lineStatus: LineStatus | null;                // DERIVED, read-only (see lineStatusOf) — the granular status users read
  earlyDefault: boolean;
  soldLoan: boolean;
  activeBkOrLitigation: boolean;
  collateralInLiquidation: boolean;
  repairDenialPending: boolean;
  approvedWrittenPlan: boolean;

  dateInitialSiteVisit: string | null;

  // Workflow field data (Purchase, OIC, Lit Plan, Extension, UPP, CPC, Charge Off)
  // now lives on the packages that own it — see LoanRequest / PackageFields. The
  // only survivors here are genuinely loan-level:
  //
  // Extract-sourced milestone dates (Loan Manager). A Purchase / Charge Off
  // decision may also set these through the workflow, but they live only here —
  // never duplicated onto a package.
  purchaseDate: string | null;          // Repurchase Date
  // The loan's principal balance when SBA purchased the guaranty. A loan-level fact,
  // not a package's: one number per loan however many purchase attempts there were, and
  // the shape the extract could supply it in (#31).
  principalBalanceAtPurchase: number | null;
  // Mirrored from the package that recorded them (see MIRRORED_TO_LOAN in the API).
  // Read-only here: the package step, or the extract, is what writes them. A loan
  // carries the latest, because the report layouts list one of each per loan and the
  // extract has nowhere else to put them.
  dateToSbaLiquidation: string | null;
  dateDemandLetterSent: string | null;
  dateChargeOffSubmitted: string | null;
  chargedOffDate: string | null;
  dateExtensionRequested: string | null;
  dateExtensionApproval: string | null;
  dateExtensionExpires: string | null;
  dateFinalSbaChargeOff: string | null;  // SBA C/O Date (USER20D)
  dateSbaUncollectible: string | null;   // Uncollectible Date (USER18D)
  accruedExpenses: number | null;   // running SBA receivable (loan-level)

  // Repair / Denial — a loan-level condition (paired with the repairDenialPending flag).
  repairAmount: number | null;
  repairReason: string | null;
  repairInvoiceReceivedDate: string | null;
  repairInvoicePaidDate: string | null;

  nextActionDate: string | null;
  action: string | null;

  // Assignments — oid + denormalized displayName from Graph.
  assignedLoanAnalystId: string | null;
  assignedLoanAnalystName: string | null;
  assignedSaOfficerId: string | null;
  assignedSaOfficerName: string | null;

  // Soft delete
  isDeleted: boolean;
  deletedAt: string | null;
  deletedByName: string | null;

  // Timestamps
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface CreateLoanRequest {
  sbaLoanNumber: string;
  umwsbLoanNumber?: string;
  borrowerName: string;
  loanType?: string;
  stateLocated?: string;
  naicsCode?: string;
  approvalGross?: number;
  guarantyBalance?: number;
  dateInLiquidation?: string;
  guarantyPct?: number;
  dateFirstDisbursed?: string;
  maturityDate?: string;
  inBusiness?: boolean;
  sbaLoanStatus?: string;
  etranLoanStatus?: string;
  legalBalance?: number;
  nextDueDate?: string;
  dateOfDefault?: string;
  interestRate?: number;
  noteDate?: string;
  earlyDefault?: boolean;
  purchaseDate?: string;
  initialNote?: string;
}

// Writable loan fields. Note both assignedLoanAnalystId AND assignedLoanAnalystName
// are writable — the client must set them together when picking from the Graph
// group-member dropdown, since the API does not call Graph itself.
export type UpdateLoanRequest = Partial<Omit<Loan,
  | 'id' | 'createdAt' | 'createdByName' | 'updatedAt' | 'updatedByName'
  | 'isDeleted' | 'deletedAt' | 'deletedByName'
>>;

export interface LoanListItem {
  id: number;
  sbaLoanNumber: string;
  umwsbLoanNumber: string | null;
  borrowerName: string;
  loanType: string | null;
  stateLocated: string | null;
  sbaLoanStatus: string | null;
  etranLoanStatus: string | null;
  phase: LoanPhase;
  closedReason: ClosedReason | null;
  lineStatus: LineStatus | null;   // derived
  hasOpenPackage: boolean;         // derived — a Requested/Pending package exists (blocks starting another)
  purchaseDate: string | null;
  legalBalance: number | null;
  nextActionDate: string | null;
  // What the officer does next, shown beside its date — the pair is how the grid
  // answers "what needs doing" (#9/#33).
  action: string | null;
  // Both the OID and the name: the grid filters on the OID (stable, and immune
  // to two people sharing a display name) while showing the name in the picker.
  assignedLoanAnalystId: string | null;
  assignedLoanAnalystName: string | null;
  assignedSaOfficerId: string | null;
  assignedSaOfficerName: string | null;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string | null;
  earlyDefault: boolean;
  activeBkOrLitigation: boolean;
}

// ---------------------------------------------------------------------------
// Enum value lists used by the per-tab dropdowns
// ---------------------------------------------------------------------------

export const REPAIR_REASONS = ['Collateral', 'Eligibility', 'Other'] as const;
export const OIC_DECISIONS = ['Approved', 'Declined', 'Declined with Counter'] as const;
export const LIT_DECISIONS = ['Approved', 'Declined', 'Other'] as const;
// Return to Regular Servicing. The Status Legend's analyst spec for it reads "SBA decision
// (approved or delcined)" -- the same sentence it gives OIC and Lit Plan -- so the two values
// are the client's, not ours. No counter-offer here: a counter means a figure for an OIC and
// nothing for a completed modification.
export const RTS_DECISIONS = ['Approved', 'Declined'] as const;
export const EXTENSION_REASONS = ['Open Bankruptcy', 'Ongoing Liquidation', 'Litigation', 'OIC', 'Workout'] as const;
export const UPP_STATUSES = ['UPP Submitted without CO', 'UPP Submitted with CO', 'UPP Submitted with CO and CPC'] as const;
// Why the loan is being purchased, captured by Special Assets on the request (#53).
// 'Other' takes no detail field: the officers asked to use Comments instead.
export const PURCHASE_REASONS = ['Delinquency', 'Bankruptcy', 'Other'] as const;

// Package types whose *request* form captures the loan's eTran + Loan Status.
// Analyst-initiated types (UPP/CPC) always have, since the analyst is the creator.
// The Rough Draft puts the pair on every Special Assets form, and now so do we.
const SA_STATUS_AT_CREATE: PackageType[] = [
  'Purchase', 'OIC', 'Lit Plan', 'Extension', 'Charge Off', 'Return to Regular Servicing',
];

export function capturesStatusAtCreate(type: PackageType): boolean {
  return PACKAGE_META[type].initiator === 'loan_analyst' || SA_STATUS_AT_CREATE.includes(type);
}
// The shape of a UPP. A UPP is another route for submitting a charge-off (and a
// CPC), opened by the analyst rather than Special Assets — see
// docs/upp-charge-off-cpc-design.md. Without a CO it is a dated compliance record
// that completes on submit; with one it behaves as a Charge Off package does.
export const UPP_WITHOUT_CO = 'UPP Submitted without CO';

// The shape of a charge-off submission, chosen by the analyst at Submit. Mirrors the
// UPP shapes: it decides whether the CPC group applies, rather than the analyst being
// asked to leave fields blank and the form having no way to know (#51).
export const CHARGE_OFF_SHAPES = ['Charge Off without CPC', 'Charge Off with CPC'] as const;

export const UPP_CO_NOT_REQUESTED_REASONS = ['Bankruptcy', 'Litigation', 'Ongoing Liquidation', 'Workout', 'OIC'] as const;
// What the officer is doing next on a loan, for the Action field (#9). Free text
// before this: the list arrived with round 2.
export const ACTIONS = [
  'Monitor Bankruptcy', 'Monitor Bankruptcy Payments', 'Monitor TPP Payments',
  'Monitor Sale of Collateral', 'Monitor for Payments', 'Monitor Legal/Litigation',
  'Monitor Modification', 'Monitor OIC',
] as const;

export const INVOICE_1544_REASONS = ['Disallowed Interest', 'Repair', 'SBA Overpayment', 'NSF', 'Other'] as const;

// Dropdown options shared across SA officer and analyst package forms.
export const ETRAN_LOAN_STATUSES = [
  'Disbursed Current', 'Liquidation Disbursed', 'SBA Charged Off', 'SBA Uncollectible', 'PIF',
] as const;
export const SBA_LOAN_STATUSES = [
  'Ongoing Liquidation', 'Open Bankruptcy', 'Litigation', 'OIC', 'Workout',
  'Ready for Charge Off', 'Charge Off Submitted', 'SBA Charged Off', 'SBA Uncollectible',
  // Past tense, matching the ClosedReason of the same name — the loan's condition, not
  // the package that got it there (#44). 'Paid in Full' is here for the same reason, and
  // is spelt that way everywhere rather than 'PIF' — settled in
  // line-status-redesign.md §9.6.3 (#45/#56).
  'Returned to Regular Servicing', 'Paid in Full',
] as const;

// A semi-annual report states the loan's condition, so it offers the same values the
// loan's own status does (#56). It used to have a vocabulary of its own — Bankruptcy,
// Liquidation, CO Submitted — which is what "SAR status doesn't match the loan status" meant.
export const SAR_STATUSES = SBA_LOAN_STATUSES;

// ---------------------------------------------------------------------------
// Semi-Annual Reports (one-to-many per loan)
// ---------------------------------------------------------------------------

export interface SemiAnnualReport {
  id: number;
  loanId: number;
  reportDate: string;
  currentStatus: string | null;
  comments: string | null;
  createdAt: string;
  createdByName: string | null;
}

export interface CreateSemiAnnualReportRequest {
  reportDate: string;
  currentStatus?: string;
  comments?: string;
}

// ---------------------------------------------------------------------------
// 1544 Invoices (one-to-many per loan)
// ---------------------------------------------------------------------------

export interface Invoice1544 {
  id: number;
  loanId: number;
  invoiceAmount: number | null;
  invoiceDate: string | null;
  dateSubmittedForPayment: string | null;
  reason: string | null;
  comments: string | null;
  createdAt: string;
  createdByName: string | null;
}

export interface CreateInvoice1544Request {
  invoiceAmount?: number;
  invoiceDate?: string;
  dateSubmittedForPayment?: string;
  reason?: string;
  comments?: string;
}

// ---------------------------------------------------------------------------
// Packages / requests — the unit of work against a loan.
//
// Every package shares one uniform lifecycle (mirrors the Status Legend's
// Request / Pending / Decisioned column):
//
//   Requested  — Special Assets asked; awaiting the Loan Analyst to prepare & send
//   Pending    — Loan Analyst submitted the package to SBA; awaiting SBA's decision
//   Decisioned — SBA responded; the Loan Analyst recorded the outcome (terminal)
//   Cancelled  — withdrawn
//
// Backed by dbo.requests. A loan has at most one open (Requested/Pending) package
// at a time — that IS "what's being worked right now." Phase-driving packages
// advance the loan's phase; see PACKAGE_META.
// ---------------------------------------------------------------------------

export type RequestStatus =
  | 'Requested' | 'Pending' | 'On Hold' | 'Decisioned' | 'Cancelled';

export const REQUEST_STATUSES: RequestStatus[] = ['Requested', 'Pending', 'On Hold', 'Decisioned', 'Cancelled'];

// A package parked so another can be worked (#52). Deliberately absent from
// OPEN_REQUEST_STATUSES below: a held package does not occupy the one-open slot, which is
// what lets any number of them sit against a loan without a single query changing meaning.
// See docs/package-hold-design.md.
export const HELD_STATUS: RequestStatus = 'On Hold';

// Only a package that hasn't reached SBA's answer can be parked.
export function canHold(status: RequestStatus): boolean {
  return status === 'Requested' || status === 'Pending';
}

// What a status change means for a held package. Resuming returns it to whatever it was
// when parked (heldFromStatus), so a Requested package resumes Requested and a Pending one
// resumes Pending.
export function isValidStatusChange(from: RequestStatus, to: RequestStatus): boolean {
  if (from === to) return true;
  if (from === 'Decisioned' || from === 'Cancelled') return false;   // terminal
  if (to === 'On Hold') return canHold(from);
  if (from === 'On Hold') return to === 'Requested' || to === 'Pending' || to === 'Cancelled';
  return true;
}

// A request is "open" (occupies the single-open slot) while Requested or Pending.
export const OPEN_REQUEST_STATUSES: RequestStatus[] = ['Requested', 'Pending'];

export type PackageType =
  | 'Purchase'
  | 'UPP'
  | 'OIC'
  | 'Lit Plan'
  | 'Extension'
  | 'Charge Off'
  | 'Return to Regular Servicing';

export const PACKAGE_TYPES: PackageType[] = [
  'Purchase', 'UPP', 'OIC', 'Lit Plan', 'Extension', 'Charge Off', 'Return to Regular Servicing',
];

// Per-type behavior. `initiator` = which role normally creates it (SA officer
// creates at 'Requested'; analyst-led types start at 'Pending'). `phaseOnCreate`
// moves the loan when the request is created; `phaseOnDecision` moves it when
// decisioned. Charge Off's closed reason is chosen at decision time (Charged Off
// vs Uncollectible), so it is not encoded here.
export interface PackageMeta {
  initiator: 'special_assets' | 'loan_analyst';
  phaseOnCreate?: LoanPhase;
  phaseOnDecision?: LoanPhase;
}

export const PACKAGE_META: Record<PackageType, PackageMeta> = {
  // Submitting the purchase request moves the loan to Liquidation immediately,
  // not when SBA's decision is recorded (UAT: "Liquidation phase is updating one
  // step late"). A loan with a purchase request in flight is being actively
  // worked, and the phase is how users find that work. `phaseOnDecision` is kept
  // as an idempotent backstop for anything that reaches a decision while still
  // sitting in Prepurchase.
  'Purchase':                     { initiator: 'special_assets', phaseOnCreate: 'Liquidation', phaseOnDecision: 'Liquidation' },
  'UPP':                          { initiator: 'loan_analyst' },
  'OIC':                          { initiator: 'special_assets' },
  'Lit Plan':                     { initiator: 'special_assets' },
  'Extension':                    { initiator: 'special_assets' },
  'Charge Off':                   { initiator: 'special_assets', phaseOnCreate: 'Charge Off', phaseOnDecision: 'Closed' },
  // Returning a loan to regular servicing does NOT close it (#45): it could default
  // again and need further packages, and a closed loan cannot be worked. The loan stays in
  // Liquidation and carries "Returned to Regular Servicing" as its Loan Status instead.
  'Return to Regular Servicing':  { initiator: 'special_assets', phaseOnDecision: 'Liquidation' },
};

// Which packages may be opened in each phase (when nothing is already open).
const LIQUIDATION_PACKAGES: PackageType[] = [
  'UPP', 'OIC', 'Lit Plan', 'Extension', 'Charge Off', 'Return to Regular Servicing',
];
export const ALLOWED_PACKAGES_BY_PHASE: Record<LoanPhase, PackageType[]> = {
  'Prepurchase': ['Purchase'],
  'Liquidation': LIQUIDATION_PACKAGES,
  // A charge-off is in flight, so nothing may be *opened* — except a UPP, which the
  // analyst may still need to file: its 45-day clock doesn't care what else is open,
  // and the no-CO shape never occupies the one-open slot at all (it completes on
  // submit). Special Assets is excluded automatically, UPP being analyst-initiated.
  'Charge Off':  ['UPP'],
  'Closed':      [],
};

// Typed workflow data OWNED BY the package (see workflow-data-ownership.md).
// Each key is a real column on dbo.requests — no JSON, no loan mirror. A given
// package type uses only the subset in PACKAGE_FIELD_KEYS[type].
export interface PackageFields {
  // Purchase (purchaseDate is a loan milestone, not a package field)
  //   Special Assets capture at request time (Rough Draft SA column, ticket #6):
  uccSearchDate?: string | null;
  siteVisitDate?: string | null;
  purchaseReason?: string | null;
  dateToSbaLiquidation?: string | null;
  datePurchaseSubmitted?: string | null;
  disallowedInterest?: number | null;
  dateDemandLetterSent?: string | null;
  // Charge Off (final SBA charge-off / uncollectible dates are loan milestones)
  //   Special Assets capture at request time (Rough Draft SA column, ticket #18):
  collateralLiquidated?: boolean | null;
  expensesIncurred?: boolean | null;
  expensesAmount?: number | null;
  referToTreasury?: boolean | null;
  dateChargeOffSubmitted?: string | null;
  // Whether this charge-off carries a CPC claim — see CHARGE_OFF_SHAPES.
  chargeOffStatus?: string | null;
  // What the analyst claimed from SBA with the charge-off package. Distinct from the
  // officer's expensesAmount above, which is what the bank incurred (#43 Q11).
  chargeOffExpensesSubmitted?: number | null;
  chargedOffDate?: string | null;
  // OIC
  //   Special Assets capture at request time (Rough Draft OIC / ticket #43):
  oicOfferor?: string | null;
  oicAmountOffered?: number | null;
  oicUmwsbApprovalDate?: string | null;
  oicSubmittedDate?: string | null;
  oicDecisionReceivedDate?: string | null;
  oicSbaDecision?: string | null;
  oicAmountApproved?: number | null;
  oicDueDate?: string | null;
  oicDatePaid?: string | null;
  // Lit Plan
  //   Special Assets capture at request time (Rough Draft Litigation Plan / #43).
  //   "Total Fees & Costs to Date" is not stored: it is the sum of the two Fees &
  //   Costs figures, which are exhaustive (approved + not approved), so the form
  //   shows it as a derived read-only total instead of a value that can disagree.
  litFeesApprovedToDate?: number | null;
  litFeesNotApprovedToDate?: number | null;
  litTotalBudgetRequested?: number | null;
  litPlanSubmittedDate?: string | null;
  litLegalExpenses?: number | null;
  litSbaDecision?: string | null;
  litAmendedPlanSubmittedDate?: string | null;
  // Extension
  //   Special Assets capture at request time (Rough Draft Extension / #43).
  //   Length is free text because the requirement says "months/years".
  extensionLength?: string | null;
  extensionExpectedResolution?: string | null;
  dateExtensionRequested?: string | null;
  dateExtensionApproval?: string | null;
  dateExtensionExpires?: string | null;
  extensionReason?: string | null;
  // UPP
  uppSubmittedDate?: string | null;
  uppStatus?: string | null;
  uppChargeOffNotRequestedReason?: string | null;
  // CPC
  cpcSubmittedDate?: string | null;
  cpcSubmittedAmount?: number | null;
  cpcReceivedDate?: string | null;
  cpcReceivedAmount?: number | null;
  cpcComments?: string | null;
  // Return to Regular Servicing
  //   Special Assets capture at request time (ticket #43's attachment; this type
  //   is absent from the Rough Draft).
  rtsModificationCompleted?: boolean | null;
  rtsSubmittedDate?: string | null;
  rtsSbaDecision?: string | null;
}

// Which PackageFields keys each package type uses. Drives per-tab rendering,
// the report derivation, and which columns the API writes.
// The CPC claim, hosted by whichever package carries it (#51). CPC is no longer a
// package type of its own: needing a second package was what made it unsubmittable
// alongside a charge-off.
const CPC_FIELD_KEYS: (keyof PackageFields)[] = [
  'cpcSubmittedDate', 'cpcSubmittedAmount',
  'cpcReceivedDate', 'cpcReceivedAmount', 'cpcComments',
];

export const PACKAGE_FIELD_KEYS: Record<PackageType, (keyof PackageFields)[]> = {
  'Purchase':                    ['uccSearchDate', 'siteVisitDate', 'purchaseReason', 'dateToSbaLiquidation', 'datePurchaseSubmitted', 'disallowedInterest', 'dateDemandLetterSent'],
  'Charge Off':                  ['collateralLiquidated', 'expensesIncurred', 'expensesAmount', 'referToTreasury', 'dateChargeOffSubmitted', 'chargeOffStatus', 'chargeOffExpensesSubmitted', 'chargedOffDate', ...CPC_FIELD_KEYS],
  'OIC':                         ['oicOfferor', 'oicAmountOffered', 'oicUmwsbApprovalDate', 'oicSubmittedDate', 'oicDecisionReceivedDate', 'oicSbaDecision', 'oicAmountApproved', 'oicDueDate', 'oicDatePaid'],
  'Lit Plan':                    ['litFeesApprovedToDate', 'litFeesNotApprovedToDate', 'litTotalBudgetRequested', 'litPlanSubmittedDate', 'litLegalExpenses', 'litSbaDecision', 'litAmendedPlanSubmittedDate'],
  'Extension':                   ['extensionLength', 'extensionExpectedResolution', 'dateExtensionRequested', 'dateExtensionApproval', 'dateExtensionExpires', 'extensionReason'],
  // A UPP carries the charge-off group when its shape says so, and the CPC group on
  // top of that. Which are *shown* is decided per shape by the form; which are
  // *storable* is this list.
  'UPP':                         ['uppSubmittedDate', 'uppStatus', 'uppChargeOffNotRequestedReason',
                                  'collateralLiquidated', 'expensesIncurred', 'expensesAmount', 'referToTreasury',
                                  'dateChargeOffSubmitted', 'chargeOffExpensesSubmitted', 'chargedOffDate',
                                  ...CPC_FIELD_KEYS],
  'Return to Regular Servicing': ['rtsModificationCompleted', 'rtsSubmittedDate', 'rtsSbaDecision'],
};

// All PackageFields keys — handy for the API to iterate columns.
export const ALL_PACKAGE_FIELD_KEYS: (keyof PackageFields)[] = [
  ...new Set(Object.values(PACKAGE_FIELD_KEYS).flat()),
];

export interface LoanRequest {
  id: number;
  loanId: number;
  // Loan fields denormalized for the list view; populated by the API via JOIN.
  sbaLoanNumber: string;
  borrowerName: string;
  status: RequestStatus;
  requestType: PackageType | null;
  description: string | null;
  submittedByName: string | null;
  submittedAt: string;
  closedAt: string | null;
  closedByName: string | null;
  closedNote: string | null;
  // Typed workflow data this package owns.
  fields: PackageFields;
}

export interface CreateRequestRequest {
  loanId: number;
  requestType: PackageType;
  description?: string;
  // Officer this package assigns to (SA-initiated). Defaults to submitter.
  assignSaOfficerId?: string;
  assignSaOfficerName?: string;
  // Analyst assignment (analyst-initiated types).
  assignAnalystId?: string;
  assignAnalystName?: string;
  // Snapshot/update of the loan's external system statuses at submission time.
  sbaLoanStatus?: string;
  etranLoanStatus?: string;
  // Typed workflow data for this package.
  fields?: PackageFields;
  // Initial status. Defaults server-side to 'Requested' for SA-initiated types
  // and 'Pending' for analyst-initiated types (see PACKAGE_META.initiator).
  initialStatus?: RequestStatus;
}

export interface UpdateRequestRequest {
  status?: RequestStatus;
  requestType?: PackageType | null;
  description?: string | null;
  closedNote?: string | null;
  // Typed workflow data to set/update on this package (submit- or decision-time).
  fields?: PackageFields;
  // Loan-level milestone dates a decision may set (written to the loan, not the
  // package): Purchase sets purchaseDate; Charge Off sets the final/uncollectible date.
  loanMilestones?: {
    purchaseDate?: string | null;
    principalBalanceAtPurchase?: number | null;
    dateFinalSbaChargeOff?: string | null;
    dateSbaUncollectible?: string | null;
  };
  // Chosen at decision time for a Charge Off package.
  closedReason?: ClosedReason;
  // Shared fields the analyst can update when advancing.
  sbaLoanStatus?: string;
  etranLoanStatus?: string;
  assignAnalystId?: string;
  assignAnalystName?: string;
}

// ---------------------------------------------------------------------------
// Loan extract import
// ---------------------------------------------------------------------------

// One row of the monthly Loan Manager extract. Deduped on umwsbLoanNumber
// (Loan Nbr) — insert-only. Loan Manager labels in parens.
export interface LoanImportRow {
  umwsbLoanNumber: string;            // Loan Nbr (dedup key, required)
  sbaLoanNumber?: string | null;      // Sba Number
  borrowerName: string;               // Short Name
  loanType?: string | null;           // Record Type - Code ('2' | '4')
  approvalGross?: number | null;      // Loan Amt
  guarantyBalance?: number | null;    // Part Bal (Bor)
  guarantyPct?: number | null;        // Sba Grty %
  noteDate?: string | null;           // Note Date (ISO YYYY-MM-DD)
  maturityDate?: string | null;       // Maturity Date
  naicsCode?: string | null;          // Naics Code (description resolved by join on read)
  partId?: string | null;             // Part Id — the participant holding the guaranteed
                                      // portion. Stored raw and used to derive soldLoan;
                                      // not returned by the API or shown in the UI.
  stateLocated?: string | null;       // Site State
  dateInLiquidation?: string | null;  // DATEINLIQUID
  purchaseDate?: string | null;       // Repurchase Date
  dateSbaUncollectible?: string | null;  // USER18D (Uncollectible Date)
  dateFinalSbaChargeOff?: string | null; // USER20D (SBA C/O Date)
  // Not imported, by decision (2026-08-21): the Column Mapping sheet asks for earlyDefault
  // to be calculated as Note Date + 18 months, but the date that has to be compared
  // against it — the date of default — is not in the extract. It stays a manual checkbox.
  // See the board's proposed #62.
}

/**
 * The whole portfolio, for the Excel exports.
 *
 * Three arrays rather than one nested shape: the workbook has a sheet per entity, so the
 * flattening is the client's business, and nesting packages under loans would send the
 * loan's identity columns once per package.
 */
export interface PortfolioExport {
  generatedAt: string;
  loans: Loan[];
  packages: LoanRequest[];
  notes: LoanNote[];
}

/** One tracked extract import, as the history list shows it. */
export interface LoanImportSummary {
  id: number;
  clientRef: string;
  fileName: string | null;
  fileHash: string | null;
  fileSize: number | null;
  /** Rows in the whole file, as the client declared it. Null for an older or un-batched run. */
  totalRows: number | null;
  batchCount: number | null;
  batchesReceived: number;
  inserted: number;
  skipped: number;
  errorCount: number;
  /** Loans still present that this import created — lower than `inserted` if any were deleted. */
  loansFromImport: number;
  startedAt: string;
  /** Null means the run never finished: fewer batches arrived than were expected. */
  completedAt: string | null;
  importedByName: string | null;
}

export interface LoanImportRequest {
  rows: LoanImportRow[];
  /**
   * Batch metadata. All optional: a caller that posts a whole file in one request can omit
   * every field and the import is still recorded, with a server-minted ref.
   *
   * `clientRef` is what groups the several POSTs of one file — the client mints it per file
   * (a UUID) and repeats it on every batch. It is a correlation key, not an identity: the
   * server keeps its own surrogate id, so a colliding or forged ref is detectable rather
   * than corrupting. Minting it client-side means the record exists before the first batch
   * is answered, so an import that dies on batch 1 still leaves a trace — and a retried
   * batch rejoins its own import instead of starting a second one.
   */
  clientRef?: string;
  /** 0-based position of this batch within the file. */
  seq?: number;
  /** How many batches the file will be sent as, so a short run is recognisable. */
  batchCount?: number;
  /** Rows in the whole file, not just this batch. */
  totalRows?: number;
  fileName?: string;
  /** SHA-256 of the uploaded bytes, hex — lets a file be matched to the import later. */
  fileHash?: string;
  fileSize?: number;
}

export interface LoanImportResult {
  inserted: number;
  updated: number;          // always 0 — import is insert-only (kept for shape compat)
  skipped: number;          // rows whose umwsbLoanNumber already exists, or invalid
  errors: { umwsbLoanNumber: string; message: string }[];
  /** The tracked import this batch belongs to (dbo.loanImports.id). */
  importId?: number;
  /** Totals for the whole file so far, across every batch received. */
  importTotals?: { inserted: number; skipped: number; errors: number };
  /** True once every batch the client said to expect has been received. */
  complete?: boolean;
}

// ---------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------

export interface LoanNote {
  id: number;
  loanId: number;
  noteText: string;
  createdAt: string;
  createdByName: string | null;
  isDeleted: boolean;
}

export interface CreateNoteRequest {
  noteText: string;
}

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

export type AuditAction = 'Create' | 'Update' | 'Delete' | 'Restore';

export interface AuditEntry {
  id: number;
  entityType: string;
  entityId: number;
  action: AuditAction;
  fieldName: string | null;
  previousValue: string | null;
  newValue: string | null;
  changedByName: string | null;
  changedAt: string;
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

export interface ApiError {
  error: string;
}

// ---------------------------------------------------------------------------
// State-machine helpers (pure) — the single source of truth for how packages
// and phases move. The API imports these; unit tests assert them directly.
// ---------------------------------------------------------------------------

// Default status a newly-created package starts in, keyed off who initiates it:
// SA-initiated packages await the analyst ('Requested'); analyst-initiated
// packages (UPP/CPC) are submitted straight to SBA ('Pending').
export function initialRequestStatus(type: PackageType): RequestStatus {
  return PACKAGE_META[type].initiator === 'special_assets' ? 'Requested' : 'Pending';
}

// May a package of this type be opened while the loan is in this phase?
export function isPackageAllowedInPhase(type: PackageType, phase: LoanPhase): boolean {
  return ALLOWED_PACKAGES_BY_PHASE[phase].includes(type);
}

// The loan phase after a package of this type is created (some packages, e.g.
// Charge Off, advance the phase on creation). Returns the unchanged phase otherwise.
export function phaseAfterCreate(
  type: PackageType, currentPhase: LoanPhase, fields?: PackageFields,
): LoanPhase {
  if (carriesChargeOff(type, fields)) return 'Charge Off';
  return PACKAGE_META[type].phaseOnCreate ?? currentPhase;
}

// Does this package carry a charge-off submission? True for a Charge Off package, and
// for a UPP whose shape includes one — which is why the phase effect can't be read
// from PACKAGE_META alone any more. See docs/upp-charge-off-cpc-design.md §7.
export function carriesChargeOff(type: PackageType, fields?: PackageFields): boolean {
  if (type === 'Charge Off') return true;
  if (type !== 'UPP') return false;
  const shape = fields?.uppStatus;
  return !!shape && shape !== UPP_WITHOUT_CO;
}

// Does submitting this package finish it? A UPP with no charge-off needs no SBA
// decision, so creating it completes it: everything it records is known at submit, and
// afterwards it is edited rather than decided (#17).
export function completesOnSubmit(type: PackageType, fields?: PackageFields): boolean {
  return type === 'UPP' && fields?.uppStatus === UPP_WITHOUT_CO;
}

/**
 * Where an imported loan starts, read from the dates the extract hands us.
 *
 * Every row used to land in 'Prepurchase' regardless, which contradicted the row's own
 * data: a loan carrying a Repurchase Date has already had its guaranty purchased, so it is
 * in liquidation, not waiting to be purchased. It also made those loans invisible — every
 * role's default view carries a `phase notEquals Prepurchase` chip (defaultFilters.ts),
 * whose stated purpose is hiding loans with nothing to work yet. Post-purchase loans have
 * plenty to work, so the default view was hiding exactly the wrong ones.
 *
 * Migration 014 fixed this for loans whose phase comes from a purchase *package*. An
 * imported loan has no package — deliberately, see the note in the handler — so it needed
 * the same rule expressed over the extract's columns instead.
 *
 * `DATEINLIQUID` on its own does NOT advance the phase. Per the client's Status Legend it
 * is recorded at "Pending Purchase", when the analyst submits the package to SBA — so a
 * loan with a liquidation date but no Repurchase Date is still awaiting purchase, which is
 * Prepurchase. Being wrong in that direction only under-advances a loan, which a user can
 * see and act on; over-advancing hides work.
 */
export function phaseFromExtract(row: {
  purchaseDate?: string | null;
  dateFinalSbaChargeOff?: string | null;
  dateSbaUncollectible?: string | null;
}): { phase: LoanPhase; closedReason: ClosedReason | null } {
  const chargedOff = row.dateFinalSbaChargeOff || null;
  const uncollectible = row.dateSbaUncollectible || null;

  // SBA has resolved it, so the loan is finished however it got here. A closure date with
  // no purchase date is contradictory input, and the closure is the stronger claim — a
  // charge-off cannot happen on a guaranty that was never purchased, so the missing
  // purchase date is the unreliable half.
  if (chargedOff || uncollectible) {
    // Both present is contradictory too. Take the later date as the operative outcome
    // rather than preferring one reason on principle.
    const reason: ClosedReason = (chargedOff && uncollectible)
      ? (chargedOff >= uncollectible ? 'SBA Charged Off' : 'SBA Uncollectible')
      : (chargedOff ? 'SBA Charged Off' : 'SBA Uncollectible');
    return { phase: 'Closed', closedReason: reason };
  }

  // Purchased, so Special Assets is working the liquidation.
  if (row.purchaseDate) return { phase: 'Liquidation', closedReason: null };

  return { phase: 'Prepurchase', closedReason: null };
}

export interface PhaseChange {
  phase: LoanPhase;
  closedReason: ClosedReason | null;
}

// The loan phase change (if any) when a package transitions to a new status.
//   Decisioned  -> the package's phaseOnDecision (with a resolved closedReason when Closed)
//   Cancelled   -> revert to the phase captured when the package opened
//   otherwise   -> null (no phase change)
export function phaseAfterStatusChange(opts: {
  requestType: PackageType;
  newStatus: RequestStatus;
  priorPhase: LoanPhase;          // phase snapshot from when the package opened
  closedReasonInput?: ClosedReason;
  fields?: PackageFields;         // the package's data: a UPP's shape decides its effect
  currentStatus?: RequestStatus;  // where it is now — a hold changes what Cancel means
}): PhaseChange | null {
  const { requestType, newStatus, priorPhase, closedReasonInput, fields, currentStatus } = opts;
  const meta = PACKAGE_META[requestType];

  // Parking a package gives back the phase it took. Otherwise a held Charge Off would
  // leave the loan in Charge Off phase, where ALLOWED_PACKAGES_BY_PHASE permits almost
  // nothing — so the officer could free the slot and still not open what they parked it
  // for. Phase tracks what is actually in flight; a held package is not.
  if (newStatus === 'On Hold') {
    return { phase: priorPhase ?? 'Liquidation', closedReason: null };
  }

  // Resuming re-applies whatever creating it did, from the same snapshot.
  if (currentStatus === 'On Hold' && (newStatus === 'Requested' || newStatus === 'Pending')) {
    const resumed = phaseAfterCreate(requestType, priorPhase ?? 'Liquidation', fields);
    return { phase: resumed, closedReason: null };
  }

  // Cancelling from a hold must NOT restore the phase a second time — the hold already
  // did. Restoring again would resurrect a phase the loan has since moved on from.
  if (newStatus === 'Cancelled' && currentStatus === 'On Hold') return null;

  // A UPP carrying a charge-off resolves the loan the way a Charge Off package does.
  if (newStatus === 'Decisioned' && carriesChargeOff(requestType, fields) && !meta.phaseOnDecision) {
    return { phase: 'Closed', closedReason: closedReasonInput ?? 'SBA Charged Off' };
  }

  if (newStatus === 'Decisioned' && meta.phaseOnDecision) {
    if (meta.phaseOnDecision === 'Closed') {
      return { phase: 'Closed', closedReason: closedReasonInput ?? 'SBA Charged Off' };
    }
    return { phase: meta.phaseOnDecision, closedReason: null };
  }
  if (newStatus === 'Cancelled') {
    return { phase: priorPhase ?? 'Liquidation', closedReason: null };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Line Status — the granular "where is this loan" status from the Status Legend.
// Two-level model: `phase` is the coarse Header Status; `lineStatus` is the Line
// Status. It is DERIVED (never stored or hand-set) from the phase, the currently
// open package, the loan's BK flag, and `closedReason`.
// See docs/2026-06-24 Requirements/line-status-redesign.md.
// ---------------------------------------------------------------------------

export type LineStatus =
  // Purchase
  | 'Purchase Request' | 'Purchase Pending'
  // OIC / Lit Plan / Extension package activity
  | 'OIC Request' | 'OIC Pending'
  | 'Lit Plan Request' | 'Lit Plan Pending'
  | 'Extension Request' | 'Extension Pending'
  // Charge Off
  | 'Charge Off Request' | 'Charge Off Pending' | 'SBA Charged Off' | 'SBA Uncollectible'
  // Return to Regular Servicing
  | 'Reg. Servicing Request' | 'Reg. Servicing Pending'
  // Conditions with no package behind them: they come from the loan's Loan Status.
  | 'Workout' | 'Returned to Regular Servicing' | 'Paid in Full'
  // Default working state between packages ('Open Bankruptcy' derived from the loan's BK flag)
  | 'Ongoing Liquidation' | 'Open Bankruptcy';

export const LINE_STATUSES: LineStatus[] = [
  'Purchase Request', 'Purchase Pending',
  'OIC Request', 'OIC Pending',
  'Lit Plan Request', 'Lit Plan Pending',
  'Extension Request', 'Extension Pending',
  'Charge Off Request', 'Charge Off Pending', 'SBA Charged Off', 'SBA Uncollectible',
  'Reg. Servicing Request', 'Reg. Servicing Pending',
  'Workout', 'Returned to Regular Servicing', 'Paid in Full',
  'Ongoing Liquidation', 'Open Bankruptcy',
];

// The Line Status a phase-relevant package projects while it is open, keyed by
// [type][Requested|Pending]. UPP is handled in lineStatusOf: only its charge-off
// shape is ever open, and the phase tells us. A decisioned package leaves no open
// row, so the loan reverts to the default working state / phase terminal.
const PACKAGE_LINE_STATUS: Partial<Record<PackageType, Partial<Record<RequestStatus, LineStatus>>>> = {
  'Purchase':                    { Requested: 'Purchase Request',       Pending: 'Purchase Pending' },
  'OIC':                         { Requested: 'OIC Request',            Pending: 'OIC Pending' },
  'Lit Plan':                    { Requested: 'Lit Plan Request',       Pending: 'Lit Plan Pending' },
  'Extension':                   { Requested: 'Extension Request',      Pending: 'Extension Pending' },
  'Charge Off':                  { Requested: 'Charge Off Request',     Pending: 'Charge Off Pending' },
  'Return to Regular Servicing': { Requested: 'Reg. Servicing Request', Pending: 'Reg. Servicing Pending' },
};

export interface LineStatusInput {
  phase: LoanPhase;
  closedReason: ClosedReason | null;
  // The loan's single open (Requested/Pending) package, if any.
  openPackage?: { type: PackageType; status: RequestStatus } | null;
  // The loan's active-bankruptcy/litigation flag — the one non-package signal
  // that colors the default working state.
  activeBkOrLitigation?: boolean;
  // The loan's own condition. Three of its values have no package behind them, so the
  // Line Status has nowhere else to learn them from (#45).
  sbaLoanStatus?: string | null;
}

// Loan Status values that are conditions in their own right rather than something a
// package projects. Kept deliberately short: everything else (OIC, Litigation, the
// charge-off states) already surfaces through its package or the phase, and reading those
// off the loan's status as well would give the same fact two sources.
const CONDITION_LINE_STATUS: Record<string, LineStatus> = {
  'Workout': 'Workout',
  'Returned to Regular Servicing': 'Returned to Regular Servicing',
};

// Derive the loan's Line Status. Pure — the single source of truth the API uses
// to populate `Loan.lineStatus` / `LoanListItem.lineStatus`. Returns null when no
// descriptive status applies yet (a fresh Prepurchase loan with no package).
export function lineStatusOf(loan: LineStatusInput): LineStatus | null {
  // Closed → the terminal status implied by how it closed.
  if (loan.phase === 'Closed') {
    if (loan.closedReason === 'SBA Uncollectible') return 'SBA Uncollectible';
    if (loan.closedReason === 'Paid in Full') return 'Paid in Full';
    return 'SBA Charged Off';
  }
  // An open phase-relevant package projects its status (UPP/CPC fall through).
  const op = loan.openPackage;
  if (op && (op.status === 'Requested' || op.status === 'Pending')) {
    // A UPP is only ever open while it carries a charge-off — the no-CO shape completes
    // on submit — and only that shape moves the loan to Charge Off phase, so the phase
    // identifies it without needing the package's fields here.
    if (op.type === 'UPP' && loan.phase === 'Charge Off') {
      return op.status === 'Requested' ? 'Charge Off Request' : 'Charge Off Pending';
    }
    const label = PACKAGE_LINE_STATUS[op.type]?.[op.status];
    if (label) return label;
  }
  // Prepurchase with no purchase package yet → no descriptive status.
  if (loan.phase === 'Prepurchase') return null;
  // Nothing pending, so the loan's own condition is the most descriptive thing left —
  // "in a Workout" or "back on regular servicing" says more than "Ongoing Liquidation"
  // (#45). An open package still wins above: that is what is actually in flight.
  const condition = loan.sbaLoanStatus ? CONDITION_LINE_STATUS[loan.sbaLoanStatus] : undefined;
  if (condition) return condition;
  // Liquidation / Charge Off with nothing open: the default working state,
  // colored by the BK flag. Other flavors (OIC, litigation, workout) surface
  // through their packages while active, not as a resting loan status.
  return loan.activeBkOrLitigation ? 'Open Bankruptcy' : 'Ongoing Liquidation';
}

// ---------------------------------------------------------------------------
// Version / build info
// ---------------------------------------------------------------------------

export interface VersionInfo {
  environment: string;
  version: string;
  commitSha: string;
  buildNumber: string;
  deployedAt: string | null;
}
